/**
 * CodeMirror inline diagnostics backed by intelligence/diagnostics.
 *
 * Renders Engine-computed diagnostics as inline blocks below the affected
 * lines (similar to "Error Lens" style), instead of wavy underlines.
 *
 * Block-level widget decorations MUST be provided via a StateField +
 * EditorView.decorations facet, NOT via ViewPlugin.decorations (CM throws
 * "Block decorations may not be specified via plugins" otherwise).
 */

import { type EditorState, type Extension, RangeSetBuilder, StateEffect, StateField } from "@codemirror/state";
import {
    Decoration,
    type DecorationSet,
    EditorView,
    ViewPlugin,
    type ViewUpdate,
    WidgetType,
} from "@codemirror/view";
import type {
    DiagnosticSeverity,
    IntelligenceDiagnostic,
} from "@magic-md/engine-client/protocol";
import { magicScanField } from "../codemirror-magic-syntax/scan-state";
import { createIntelligenceSessionResolver } from "./session";
import type { IntelligenceConfig } from "./types";

/* ── Severity → CSS class ── */

const severityClass: Record<DiagnosticSeverity, string> = {
    error: "cm-diag-error",
    warning: "cm-diag-warning",
    info: "cm-diag-info",
    hint: "cm-diag-hint",
};

const severityIcon: Record<DiagnosticSeverity, string> = {
    error: "✕",
    warning: "▲",
    info: "ℹ",
    hint: "💡",
};

/* ── Widget ── */

class DiagnosticWidget extends WidgetType {
    constructor(
        private readonly diags: { severity: DiagnosticSeverity; message: string; code?: string }[],
    ) {
        super();
    }

    eq(other: DiagnosticWidget): boolean {
        if (this.diags.length !== other.diags.length) return false;
        return this.diags.every(
            (d, i) => d.message === other.diags[i].message && d.severity === other.diags[i].severity,
        );
    }

    toDOM(): HTMLElement {
        const container = document.createElement("div");
        container.className = "cm-diag-inline-container";

        for (const diag of this.diags) {
            const row = document.createElement("div");
            row.className = `cm-diag-inline ${severityClass[diag.severity]}`;

            const icon = document.createElement("span");
            icon.className = "cm-diag-inline-icon";
            icon.textContent = severityIcon[diag.severity];
            row.appendChild(icon);

            const msg = document.createElement("span");
            msg.className = "cm-diag-inline-msg";
            msg.textContent = diag.message;
            row.appendChild(msg);

            if (diag.code) {
                const code = document.createElement("span");
                code.className = "cm-diag-inline-code";
                code.textContent = diag.code;
                row.appendChild(code);
            }

            container.appendChild(row);
        }

        return container;
    }

    ignoreEvent(): boolean {
        return true;
    }
}

/* ── Build decorations from diagnostics ── */

/**
 * Compute the set of 1-based line numbers that fall inside a meta/content/use
 * block where the cursor is currently positioned (L2 editing mode).
 * Diagnostics on interior lines are suppressed to avoid transient errors while editing.
 * The directive header stays visible because block-level issues such as
 * missing required ::use inputs are anchored there.
 */
function getActiveSuppressedLines(state: EditorState): Set<number> {
    const { scan } = state.field(magicScanField);

    // Collect all block ranges from the cached scan
    const allBlocks: { startLine: number; endLine: number }[] = [
        ...scan.metaBlocks,
        ...scan.contentBlocks,
        ...scan.useBlocks,
    ];
    if (allBlocks.length === 0) return new Set();

    // Determine which lines the cursor is on
    const cursorLines = new Set<number>();
    for (const range of state.selection.ranges) {
        cursorLines.add(state.doc.lineAt(range.head).number);
    }

    const suppressedLines = new Set<number>();
    for (const block of allBlocks) {
        let active = false;
        for (const cl of cursorLines) {
            if (cl >= block.startLine && cl <= block.endLine) {
                active = true;
                break;
            }
        }
        if (active) {
            for (let ln = block.startLine + 1; ln <= block.endLine; ln++) {
                suppressedLines.add(ln);
            }
        }
    }
    return suppressedLines;
}

function buildDiagDecorations(
    state: EditorState,
    diagnostics: IntelligenceDiagnostic[],
): DecorationSet {
    if (diagnostics.length === 0) return Decoration.none;

    // Suppress diagnostics inside active meta/content/use blocks (L2 editing)
    const activeMetaLines = getActiveSuppressedLines(state);

    // Suppress diagnostics inside output fences (opaque serialized data)
    const outputFenceLineSet = state.field(magicScanField).outputFenceLines;

    // Group diagnostics by line number (1-based)
    const byLine = new Map<number, { severity: DiagnosticSeverity; message: string; code?: string }[]>();
    for (const diag of diagnostics) {
        // Skip diagnostics whose line is beyond the end of the document
        if (diag.range.startLine > state.doc.lines) continue;
        const lineNum = diag.range.startLine;
        // Skip diagnostics whose start line falls inside an active meta block
        if (activeMetaLines.has(lineNum)) continue;
        // Skip diagnostics inside output fences
        if (outputFenceLineSet.has(lineNum)) continue;
        let list = byLine.get(lineNum);
        if (!list) {
            list = [];
            byLine.set(lineNum, list);
        }
        list.push({ severity: diag.severity, message: diag.message, code: diag.code });
    }

    // Sort by line position and create decorations
    const sortedLines = [...byLine.keys()].sort((a, b) => a - b);
    const builder = new RangeSetBuilder<Decoration>();

    for (const lineNum of sortedLines) {
        const line = state.doc.line(lineNum);
        const diags = byLine.get(lineNum) ?? [];
        builder.add(
            line.to,
            line.to,
            Decoration.widget({
                widget: new DiagnosticWidget(diags),
                block: true,
                side: 1,
            }),
        );
    }

    return builder.finish();
}

/* ── Theme ── */

const diagnosticTheme = EditorView.baseTheme({
    ".cm-diag-inline-container": {
        padding: "0",
        margin: "0",
    },
    ".cm-diag-inline": {
        display: "flex",
        alignItems: "center",
        gap: "5px",
        padding: "2px 10px 2px 22px",
        fontSize: "11px",
        lineHeight: "1.5",
        fontFamily: "'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, Monaco, monospace",
    },
    ".cm-diag-inline-icon": {
        flexShrink: "0",
        fontSize: "11px",
        width: "14px",
        textAlign: "center",
    },
    ".cm-diag-inline-msg": {
        flex: "1",
        minWidth: "0",
    },
    ".cm-diag-inline-code": {
        fontSize: "11px",
        opacity: "0.5",
        marginLeft: "8px",
        flexShrink: "0",
    },
    /* Error */
    ".cm-diag-error": {
        color: "var(--color-destructive, #e55)",
        background: "color-mix(in srgb, var(--color-destructive, #e55) 6%, transparent)",
    },
    ".cm-diag-error .cm-diag-inline-icon": {
        color: "var(--color-destructive, #e55)",
    },
    /* Warning */
    ".cm-diag-warning": {
        color: "var(--color-warning, #e5a820)",
        background: "color-mix(in srgb, var(--color-warning, #e5a820) 6%, transparent)",
    },
    ".cm-diag-warning .cm-diag-inline-icon": {
        color: "var(--color-warning, #e5a820)",
    },
    /* Info */
    ".cm-diag-info": {
        color: "var(--color-muted-foreground, #888)",
        background: "color-mix(in srgb, var(--color-muted-foreground, #888) 5%, transparent)",
    },
    ".cm-diag-info .cm-diag-inline-icon": {
        color: "var(--color-muted-foreground, #888)",
    },
    /* Hint */
    ".cm-diag-hint": {
        color: "var(--color-muted-foreground, #888)",
        background: "transparent",
        opacity: "0.7",
    },
    ".cm-diag-hint .cm-diag-inline-icon": {
        color: "var(--color-muted-foreground, #888)",
    },
});

/* ── StateEffect to push new diagnostics into the StateField ── */

const setDiagnostics = StateEffect.define<IntelligenceDiagnostic[]>();
const setDiagnosticsEnabled = StateEffect.define<boolean>();

let inlineDiagnosticsEnabled = true;

interface DiagnosticsRequestToken {
    sessionId: string;
    version: number;
}

export function createDiagnosticsRequestTracker(initialSessionId: string | null = null) {
    let activeSessionId = initialSessionId;
    let version = 0;

    return {
        onSessionChange(sessionId: string | null) {
            if (sessionId === activeSessionId) return;
            activeSessionId = sessionId;
            version += 1;
        },
        invalidate() {
            version += 1;
        },
        beginRequest(sessionId: string): DiagnosticsRequestToken {
            activeSessionId = sessionId;
            version += 1;
            return { sessionId, version };
        },
        shouldApply(token: DiagnosticsRequestToken, currentSessionId: string | null): boolean {
            return (
                token.version === version &&
                token.sessionId === currentSessionId &&
                currentSessionId === activeSessionId
            );
        },
    };
}

/* ── Factory ── */

/**
 * Create a CodeMirror extension that renders diagnostics inline below
 * affected lines (no wavy underlines). Debounces engine calls on doc changes.
 *
 * Uses StateField + EditorView.decorations facet for block widgets (required
 * by CM) and a ViewPlugin solely for debounced engine refresh.
 */
export function intelligenceDiagnostics(config: IntelligenceConfig): Extension {
    const resolveSession = createIntelligenceSessionResolver(config);
    const getSyncVersion = () => config.getSyncVersion?.() ?? 0;

    // Raw diagnostics storage — updated only when engine calls return.
    const rawField = StateField.define<IntelligenceDiagnostic[]>({
        create() {
            return [];
        },
        update(diags, tr) {
            for (const effect of tr.effects) {
                if (effect.is(setDiagnostics)) return effect.value;
                if (effect.is(setDiagnosticsEnabled) && !effect.value) return [];
            }
            if (tr.docChanged) return [];
            return diags;
        },
    });

    // Decoration StateField — rebuilds when diagnostics change OR cursor moves
    // (to apply/remove L2 suppression inside active meta blocks).
    // Block decorations CANNOT go through ViewPlugin.decorations.
    const field = StateField.define<DecorationSet>({
        create() {
            return Decoration.none;
        },
        update(decos, tr) {
            let enabled = inlineDiagnosticsEnabled;
            let newDiags = false;
            for (const effect of tr.effects) {
                if (effect.is(setDiagnostics)) {
                    newDiags = true;
                    break;
                }
                if (effect.is(setDiagnosticsEnabled)) {
                    enabled = effect.value;
                    if (!enabled) {
                        return Decoration.none;
                    }
                    newDiags = true;
                }
            }
            if (!enabled) {
                return Decoration.none;
            }
            // Rebuild when diagnostics change or cursor moves (for L2 suppression)
            if (newDiags || tr.selection || tr.docChanged) {
                const diags = tr.state.field(rawField);
                return buildDiagDecorations(tr.state, diags);
            }
            // Map positions through doc changes
            return decos.map(tr.changes);
        },
        provide: (f) => EditorView.decorations.from(f),
    });

    // ViewPlugin handles debounced engine calls — no decorations accessor
    const refresher = ViewPlugin.define((view) => {
        let pending: ReturnType<typeof setTimeout> | null = null;
        const tracker = createDiagnosticsRequestTracker(config.getSessionId());
        let lastAppliedKey: string | null = null;
        const clearDiagnostics = (targetView: EditorView) => {
            lastAppliedKey = null;
            targetView.dispatch({ effects: setDiagnostics.of([]) });
        };
        const applyEnabledState = (targetView: EditorView, enabled: boolean) => {
            inlineDiagnosticsEnabled = enabled;
            targetView.dispatch({ effects: setDiagnosticsEnabled.of(enabled) });
            if (!enabled) {
                clearDiagnostics(targetView);
            } else {
                scheduleRefresh(targetView, 0);
            }
        };

        const scheduleRefresh = (targetView: EditorView, delay: number) => {
            if (pending) clearTimeout(pending);
            pending = setTimeout(() => {
                pending = null;
                refresh(targetView);
            }, delay);
        };

        function refresh(v: EditorView) {
            if (!inlineDiagnosticsEnabled) {
                clearDiagnostics(v);
                return;
            }
            const engine = config.getEngine();
            const sessionId = config.getSessionId();
            tracker.onSessionChange(sessionId);
            if (!engine?.intelligenceDiagnostics) {
                clearDiagnostics(v);
                return;
            }

            resolveSession()
                .then((session) => {
                    if (!session) {
                        clearDiagnostics(v);
                        return null;
                    }

                    const diagnostics = session.engine.intelligenceDiagnostics;
                    if (!diagnostics) {
                        clearDiagnostics(v);
                        return null;
                    }

                    const requestKey = `${session.sessionId}:${getSyncVersion()}:${config.getLocale?.() ?? ""}`;
                    if (requestKey === lastAppliedKey) {
                        return null;
                    }
                    const request = tracker.beginRequest(session.sessionId);

                    return diagnostics({
                        sessionId: session.sessionId,
                        locale: config.getLocale?.(),
                    })
                        .then((result) => ({
                            request,
                            requestKey,
                            sessionId: session.sessionId,
                            result,
                        }));
                })
                .then((result) => {
                    if (!result) return;
                    if (!tracker.shouldApply(result.request, config.getSessionId())) return;
                    lastAppliedKey = result.requestKey;
                    const diags = result.result?.diagnostics ?? [];
                    v.dispatch({ effects: setDiagnostics.of(diags) });
                })
                .catch(() => {
                    clearDiagnostics(v);
                });
        }

        const unsubscribe = config.subscribeSessionId?.((sessionId) => {
            tracker.onSessionChange(sessionId);
            if (pending) {
                clearTimeout(pending);
                pending = null;
            }
            clearDiagnostics(view);
            if (sessionId) {
                scheduleRefresh(view, 0);
            }
        });
        const unsubscribeSyncVersion = config.subscribeSyncVersion?.(() => {
            if (!inlineDiagnosticsEnabled || !config.getSessionId()) return;
            tracker.invalidate();
            scheduleRefresh(view, 0);
        });

        const handleToggle = (event: Event) => {
            const detail = (event as CustomEvent<{ enabled?: boolean }>).detail;
            applyEnabledState(view, detail?.enabled !== false);
        };
        window.addEventListener(
            "magic-md:show-inline-diagnostics",
            handleToggle,
        );

        if (inlineDiagnosticsEnabled && config.getSessionId()) {
            scheduleRefresh(view, 0);
        }

        return {
            update(update: ViewUpdate) {
                void update;
            },
            destroy() {
                if (pending) clearTimeout(pending);
                unsubscribe?.();
                unsubscribeSyncVersion?.();
                window.removeEventListener(
                    "magic-md:show-inline-diagnostics",
                    handleToggle,
                );
            },
        };
    });

    return [rawField, field, refresher, diagnosticTheme];
}
